/**
 * One-call opener for a race plan's settings modal — the interlink pass (U4)
 * puts a settings gear on every surface that shows plan context (plans card,
 * plan-detail context bar), so the choreography lives here
 * once: settings modal, whose "Edit fueling profile →" row closes it and opens
 * Preferences at the Fueling section (close-then-open, never stacked — R7).
 */
import { useCallback } from "react";
import { modalBack, useModal } from "../../context/ModalProvider";
import { useToast } from "../../context/ToastProvider";
import { JourneySettingsModal } from "./JourneySettingsModal";
import { PreferencesModal } from "../modals/PreferencesModal";
import { useEventRoleActions } from "../Planner/hooks/useEventRoleActions";
import { useEventSetup } from "../Planner/hooks/useEventSetup";
import { userFacingError } from "../../lib/api";
import { journeyRaceName, type JourneyView } from "../../lib/journeyApi";

export function useOpenPlanSettings(): (
  journey: JourneyView,
  onBack?: () => void
) => void {
  const { open, close } = useModal();
  const { push: pushToast } = useToast();
  // Lane D/G (add-event surfacing §5): the shared settings opener for non-host
  // surfaces (plans card, plan-detail bar) gets the SAME role/
  // rolling actions as the My Fuel host — demote this race's goal → tune-up (one
  // shared collision-aware handler) and "switch to just training" (archive, then
  // ensure a rolling journey). Reuses the canonical hooks (no new reclassify/
  // create path here).
  const { changeEventRole, promoteMilestone } = useEventRoleActions();
  const { createRollingJourney } = useEventSetup();
  return useCallback(
    (journey: JourneyView, onBack?: () => void) => {
      const id = open(
        <JourneySettingsModal
          journey={journey}
          onClose={() => close(id)}
          onBack={modalBack(() => close(id), onBack)}
          // Lane D — demote THIS race's goal → tune-up. Customer-only: the modal
          // hides the action for an anon race plan (no `customerEventId`). Close-
          // then-act (R7): the shared handler may open the RoleResolutionSheet on
          // a collision.
          onDemoteAnchor={
            journey.customerEventId
              ? () => {
                  close(id);
                  void changeEventRole({
                    eventId: journey.customerEventId!,
                    name: journeyRaceName(journey),
                    ymd: journey.raceYmd,
                    from: "goal",
                    to: "milestone"
                  });
                }
              : undefined
          }
          // Promote a nested tune-up back to its own goal race — the SAME action
          // the My Fuel host wires, via the shared role hook (collision-aware).
          // (Remove is intentionally left to the My Fuel host: it needs the
          // archive-vs-keep guard + event list this lightweight opener doesn't hold.)
          onPromoteMilestone={(m) => {
            close(id);
            promoteMilestone(m);
          }}
          // Lane G — drop the race, keep fueling. The modal archives first, then
          // calls this; we ensure a rolling journey (idempotent) + toast. The
          // modal is already torn down (close-then-act) before the create lands.
          onSwitchToRolling={() => {
            close(id);
            void createRollingJourney()
              .then(() => {
                pushToast({
                  tone: "success",
                  message: "Back to just training — we'll keep fueling your workouts."
                });
              })
              .catch((err) => {
                pushToast({
                  tone: "error",
                  message: userFacingError(
                    err,
                    "We couldn't restart your training fuel — try again from your timeline."
                  )
                });
              });
          }}
          onOpenFuelingProfile={() => {
            close(id);
            const prefId = open(
              <PreferencesModal onClose={() => close(prefId)} initialSection="fueling" />
            );
          }}
        />
      );
    },
    [open, close, changeEventRole, promoteMilestone, createRollingJourney, pushToast]
  );
}

/** Opens Preferences directly at the Fueling section (ramp/sodium home, D5). */
export function useOpenFuelingProfile(): () => void {
  const { open, close } = useModal();
  return useCallback(() => {
    const id = open(<PreferencesModal onClose={() => close(id)} initialSection="fueling" />);
  }, [open, close]);
}
